/**
 * Abstract base class for date manipulation across different date libraries. Generic type D represents the date type
 * (e.g., Date for native, DateTime for Luxon).
 *
 * Implementations must provide all date operations in a locale-aware manner.
 */
export abstract class DateAdapter<D> {
  /** Current locale code (e.g., 'en-US', 'es-ES'). */
  protected locale = 'en-US';

  /** Sets the locale for date operations. Should update all locale-dependent operations like formatting and month names. */
  abstract setLocale(locale: string): void;

  /** Gets the current locale code. */
  getLocale(): string {
    return this.locale;
  }

  // Date Creation & Cloning

  /** Creates a date instance from year, month (0-indexed), and day. */
  abstract create(year: number, month: number, date: number): D;

  /** Creates a date instance representing today. */
  abstract today(): D;

  /** Creates a copy of the given date. */
  abstract clone(date: D): D;

  // Parsing & Formatting

  /**
   * Parses a date string according to the given format patterns. Returns null if parsing fails.
   *
   * @param value - Text to parse
   * @param parseFormats - Format patterns to try (in order)
   */
  abstract parse(value: string, parseFormats: string[]): D | null;

  /**
   * Formats a date according to the given format pattern.
   *
   * @param date - Date to format
   * @param displayFormat - Format pattern
   */
  abstract format(date: D, displayFormat: string): string;

  // Date Component Getters

  /** Gets the year of the given date. */
  abstract getYear(date: D): number;

  /** Gets the month (0-indexed) of the given date. */
  abstract getMonth(date: D): number;

  /** Gets the day of the month (1-indexed) of the given date. */
  abstract getDate(date: D): number;

  /** Gets the day of the week (0 = Sunday, 6 = Saturday) of the given date. */
  abstract getDayOfWeek(date: D): number;

  // Date Arithmetic

  /** Adds the specified number of years to the given date. */
  abstract addCalendarYears(date: D, years: number): D;

  /** Adds the specified number of months to the given date. */
  abstract addCalendarMonths(date: D, months: number): D;

  /** Adds the specified number of days to the given date. */
  abstract addCalendarDays(date: D, days: number): D;

  // Date Comparison

  /** Compares two dates. Returns negative if first < second, positive if first > second, 0 if equal. */
  abstract compareDate(first: D, second: D): number;

  /** Checks if two dates represent the same day (ignoring time). */
  abstract sameDate(first: D | null, second: D | null): boolean;

  /** Checks if a date is valid. */
  abstract isValid(date: D): boolean;

  // Locale-Dependent Data

  /** Gets the first day of the week (0 = Sunday, 1 = Monday, etc.) for the current locale. */
  abstract getFirstDayOfWeek(): number;

  /** Gets localized month names (full, e.g., "January"). Returns array of 12 names. */
  abstract getMonthNames(style: 'long' | 'short' | 'narrow'): string[];

  /** Gets localized weekday names (e.g., "Monday"). Returns array of 7 names starting from locale's first day of week. */
  abstract getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[];

  /** Gets the number of days in the given month. */
  abstract getNumDaysInMonth(date: D): number;

  /** Creates a date representing the first day of the given month. */
  abstract getFirstDateOfMonth(date: D): D;

  /** Creates a date representing the last day of the given month. */
  abstract getLastDateOfMonth(date: D): D;

  /** Converts a date to an ISO 8601 string (YYYY-MM-DD). */
  abstract toIso8601(date: D): string;

  /** Parses an ISO 8601 string (YYYY-MM-DD) to a date. */
  abstract fromIso8601(iso: string): D | null;
}
